import { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth'
import { oauthApi } from '@/api/endpoints/oauth'
import { ApiError } from '@/api/client'
import { AppLoading } from '@/components/AppLoading'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { createLogger } from '@/utils/logger'

const logger = createLogger('OAuthAuthorize')

/**
 * Interactive half of the OAuth 2.1 authorization endpoint (MCP connectors:
 * claude.ai / Claude Desktop / Claude Code). api-service validates the
 * connector's GET /api/v1/oauth/authorize and 302-bounces the browser here
 * with the query string intact. This page:
 *
 *  1. sends an unauthenticated user through the existing /login (returnTo
 *     brings them back here — Google SSO included, nothing duplicated);
 *  2. shows a minimal consent card (client name via the authed lookup);
 *  3. POSTs the decision with the Bearer — approve mints the code, deny the
 *     access_denied redirect — and follows the server-built redirect_url.
 *
 * The server re-validates everything on the POST (registered client, exact
 * redirect_uri, PKCE, resource), so a crafted deep link to this route can
 * show a card but can never mint a code or redirect anywhere unregistered.
 */
export default function OAuthAuthorize({
  isInitializing,
  isAuthenticated,
}: {
  isInitializing: boolean
  isAuthenticated: boolean
}) {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const user = useAuthStore((state) => state.user)
  const [redirecting, setRedirecting] = useState(false)

  const clientId = searchParams.get('client_id') ?? ''
  const redirectUri = searchParams.get('redirect_uri') ?? ''
  const scope = searchParams.get('scope') ?? ''
  const resource = searchParams.get('resource') ?? ''

  const clientQuery = useQuery({
    queryKey: ['oauth-client', clientId],
    queryFn: () => oauthApi.getClient(clientId),
    enabled: !isInitializing && isAuthenticated && clientId !== '',
  })

  const decision = useMutation({
    mutationFn: (action: 'approve' | 'deny') =>
      oauthApi.decide({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: searchParams.get('response_type') ?? '',
        code_challenge: searchParams.get('code_challenge') ?? '',
        code_challenge_method: searchParams.get('code_challenge_method') ?? '',
        resource,
        scope,
        state: searchParams.get('state') ?? '',
        action,
      }),
    onSuccess: (res) => {
      // replace(), not assign(): the consent page must not sit in history —
      // "back" from the connector would re-show a card whose params already
      // produced a (single-use) code.
      setRedirecting(true)
      window.location.replace(res.redirect_url)
    },
    onError: (err) => {
      logger.error('OAuth consent decision failed', err)
    },
  })

  // Wait for auth hydration before any redirect decision (Safari rehydrates
  // the persisted store slowly; bouncing to /login prematurely would lose the
  // flow for an actually-logged-in user).
  if (isInitializing) {
    return <AppLoading phase="initializing" />
  }

  if (!isAuthenticated) {
    // Reuse the whole existing login surface (password + Google SSO +
    // verification recovery). returnTo is same-host by construction and
    // re-validated by getSafeReturnTo on the way back.
    return <Navigate to={`/login?returnTo=${encodeURIComponent(window.location.href)}`} replace />
  }

  // A hand-typed or truncated link: nothing to consent to. The server is the
  // authority for full validation, but a request missing any core parameter
  // can never be approved — showing the consent card for it would only walk
  // the user into a dead click, so fail the page up front.
  // `resource` (RFC 8707) is intentionally NOT required: claude.ai's web
  // connector omits it, and the server defaults an omitted resource to this
  // AS's single /mcp resource (validateAuthorize). Gating on it here dead-ended
  // exactly those requests on the consent card.
  const malformed =
    clientId === '' ||
    redirectUri === '' ||
    searchParams.get('response_type') !== 'code' ||
    (searchParams.get('code_challenge') ?? '') === '' ||
    searchParams.get('code_challenge_method') !== 'S256'

  // A 4xx from the decision POST is a permanent validation verdict (e.g.
  // unregistered redirect_uri) — retrying can never succeed, so show the
  // broken-link copy instead of the transient try-again copy.
  const decisionPermanentlyRejected =
    decision.isError && decision.error instanceof ApiError && decision.error.status < 500

  // Scope vocabulary lives in the i18n catalogue alone; an unmapped scope
  // shows its raw token rather than hiding what is being granted.
  const scopeDescription =
    scope !== '' && i18n.exists(`oauthConsent.scopes.${scope}`)
      ? t(`oauthConsent.scopes.${scope}`)
      : scope

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-8 shadow-sm border border-neutral-200">
        {malformed || clientQuery.isError ? (
          <>
            <h1 className="text-xl font-display font-semibold text-neutral-900">
              {t('oauthConsent.errorTitle')}
            </h1>
            <p className="mt-3 text-sm text-neutral-600">
              {malformed ? t('oauthConsent.invalidRequest') : t('oauthConsent.unknownClient')}
            </p>
          </>
        ) : clientQuery.isPending ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-6 w-6" />
          </div>
        ) : (
          <>
            <h1 className="text-xl font-display font-semibold text-neutral-900">
              {t('oauthConsent.heading', {
                clientName: clientQuery.data.client_name || t('oauthConsent.unnamedClient'),
              })}
            </h1>
            <p className="mt-3 text-sm text-neutral-600">
              {t('oauthConsent.lead', { email: user?.email ?? '' })}
            </p>
            <dl className="mt-6 divide-y divide-neutral-100 text-sm">
              {scopeDescription !== '' && (
                <div className="flex justify-between gap-4 py-2.5">
                  <dt className="text-neutral-500">{t('oauthConsent.labelAccess')}</dt>
                  <dd className="font-medium text-neutral-900 text-right">{scopeDescription}</dd>
                </div>
              )}
              {resource !== '' && (
                <div className="flex justify-between gap-4 py-2.5">
                  <dt className="text-neutral-500">{t('oauthConsent.labelResource')}</dt>
                  <dd className="font-medium text-neutral-900 text-right break-all">{resource}</dd>
                </div>
              )}
            </dl>
            {decision.isError && (
              <p className="mt-4 text-sm text-destructive" role="alert">
                {decisionPermanentlyRejected
                  ? t('oauthConsent.invalidRequest')
                  : t('oauthConsent.submitError')}
              </p>
            )}
            <div className="mt-6 flex gap-3">
              <Button
                variant="secondary"
                size="pill"
                className="flex-1"
                disabled={decision.isPending || redirecting}
                onClick={() => decision.mutate('deny')}
              >
                {t('oauthConsent.deny')}
              </Button>
              <Button
                size="pill"
                className="flex-1"
                disabled={decision.isPending || redirecting}
                onClick={() => decision.mutate('approve')}
              >
                {decision.isPending || redirecting ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  t('oauthConsent.approve')
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
